/**
 * Whether an item write must carry a sales category — the ONE rule every item
 * editor applies: management's item modal and quick-create sheet (through
 * `itemService.createItem` / `updateItem`) and the till's menu editor (through
 * `MenuEditService`).
 *
 * Why this exists: reports file a line under its item's `salesCategoryId`, and
 * a line with none reads "Uncategorized". The editors let an item be saved with
 * no category — the modal offered "No Category", the quick-create sheet had no
 * field, the till could not set one — so House of Pho's Lunch Special and Bowl
 * Add On ($2,059.70, Sep 10..Oct 17) reported as Uncategorized.
 *
 * The rule (owner, 2026-10-18 — the same shape as the till's open-item Kind):
 *  - required only where the location HAS a live category. A location with
 *    none must still be able to save, and no category is ever invented;
 *  - a CREATE must name one;
 *  - an EDIT is refused only when it CLEARS the category (`null`, or any value
 *    that is not a category id). An edit that does not mention it — `undefined`
 *    is "not mentioned", which `updateItem` strips — is never refused, so a
 *    legacy uncategorized item stays editable by everything that does not touch
 *    its category;
 *  - whatever is named must be one of the location's categories (archived
 *    included, so an item already filed under one still saves): an id that
 *    resolves to nothing reports as Uncategorized all the same. Judged only
 *    against a list that has categories in it — an empty one is also what a
 *    form holds while the list is still loading, and a valid item must not be
 *    refused for that.
 */
export interface SalesCategoryFacts {
  readonly salesCategoryId?: number | null;
  readonly isArchived?: boolean | null;
}

export const SALES_CATEGORY_REQUIRED_MESSAGE =
  "Choose a sales category. Without one, this item's sales report as Uncategorized.";

export const SALES_CATEGORY_UNKNOWN_MESSAGE =
  "That sales category is not one of this location's. Choose one from the list.";

/** A stored category id: a positive integer (`0` is the categories page's unsaved-row sentinel). */
export function isSalesCategoryId(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

/** True when the location has at least one live (not archived) category. */
export function locationRequiresSalesCategory(
  categories: ReadonlyArray<SalesCategoryFacts>,
): boolean {
  return categories.some(
    (c) => c.isArchived !== true && isSalesCategoryId(c.salesCategoryId),
  );
}

/**
 * The message an item write is refused with, or `null` when it may proceed.
 * `fields` is what the write sends: the whole new item on a create, the patch
 * on an edit.
 */
export function itemSalesCategoryIssue(input: {
  readonly categories: ReadonlyArray<SalesCategoryFacts>;
  readonly fields: Readonly<Record<string, unknown>>;
  readonly isCreate: boolean;
}): string | null {
  const { categories, fields, isCreate } = input;
  const value = fields.salesCategoryId;
  if (isSalesCategoryId(value)) {
    return categories.length === 0 ||
      categories.some((c) => c.salesCategoryId === value)
      ? null
      : SALES_CATEGORY_UNKNOWN_MESSAGE;
  }
  if (!locationRequiresSalesCategory(categories)) return null;
  if (isCreate) return SALES_CATEGORY_REQUIRED_MESSAGE;
  return value === undefined ? null : SALES_CATEGORY_REQUIRED_MESSAGE;
}
